import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/site";

const lines = ["Restore.", "Recover.", "Rebuild."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18, delayChildren: 0.3 } },
};
const lineWrap = { hidden: {}, show: {} };
const lineReveal = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Restoration crew responding to a disaster" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-black" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-black/60" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 w-full pb-20 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-gold">24/7 Emergency Restoration · {CONTACT.area}</span>
        </motion.div>

        <motion.h1 variants={container} initial="hidden" animate="show" className="font-display font-black leading-[0.92] text-6xl sm:text-7xl lg:text-[8.5rem]">
          {lines.map((l, i) => (
            <span key={l} className="block overflow-hidden">
              <motion.span variants={lineReveal} className={`block ${i === 2 ? "text-gradient-gold" : "text-[#F5F5F0]"}`}>
                {l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-md text-[#A3A3A3] text-base sm:text-lg leading-relaxed"
          >
            When water, fire, mold or storm strikes, every minute matters. Our certified crews respond around the clock to restore your home or business.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="flex flex-wrap gap-4"
          >
            <a href={CONTACT.phoneHref} className="group inline-flex items-center gap-3 bg-gold text-black font-semibold px-8 py-4 hover:bg-gold-light transition-colors duration-300" data-testid="hero-call-btn">
              <Phone className="h-5 w-5" /> Call Now — {CONTACT.phone}
            </a>
            <a href="#quote" className="group inline-flex items-center gap-3 border border-gold text-gold font-semibold px-8 py-4 hover:bg-gold hover:text-black transition-colors duration-300" data-testid="hero-quote-btn">
              Request Service
              <ArrowDown className="h-5 w-5 group-hover:translate-y-1 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
